import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  where,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Staff Service - Handles operations related to brand staff management
 */
const staffService = {
  /**
   * Get all staff members for a brand
   * @param {string} brandId - Brand ID
   * @returns {Promise<Array>} - Array of staff members
   */
  getStaffByBrandId: async (brandId) => {
    try {
      const staffRef = collection(db, 'brands', brandId, 'staff');
      const querySnapshot = await getDocs(staffRef);
      
      const staffMembers = [];
      querySnapshot.forEach((doc) => {
        staffMembers.push({ id: doc.id, ...doc.data() });
      });
      
      return staffMembers;
    } catch (error) {
      console.error('Error getting staff members:', error);
      throw error;
    }
  },

  /**
   * Get a staff member by ID
   * @param {string} brandId - Brand ID
   * @param {string} staffId - Staff ID
   * @returns {Promise<object|null>} - Staff member data or null if not found
   */
  getStaffById: async (brandId, staffId) => {
    try {
      const staffRef = doc(db, 'brands', brandId, 'staff', staffId);
      const staffDoc = await getDoc(staffRef);
      
      if (staffDoc.exists()) {
        return { id: staffDoc.id, ...staffDoc.data() };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting staff member:', error);
      throw error;
    }
  },

  /**
   * Add a new staff member
   * @param {string} brandId - Brand ID
   * @param {object} staffData - Staff member data
   * @returns {Promise<object>} - Created staff member
   */
  addStaffMember: async (brandId, staffData) => {
    try {
      const staffRef = collection(db, 'brands', brandId, 'staff');
      
      // Add default fields
      const dataToSave = {
        ...staffData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: staffData.status || 'active'
      };
      
      const docRef = await addDoc(staffRef, dataToSave);
      return { id: docRef.id, ...dataToSave };
    } catch (error) {
      console.error('Error adding staff member:', error);
      throw error;
    }
  },

  /**
   * Update a staff member
   * @param {string} brandId - Brand ID
   * @param {string} staffId - Staff ID
   * @param {object} staffData - Staff member data to update
   * @returns {Promise<object>} - Updated staff member
   */
  updateStaffMember: async (brandId, staffId, staffData) => {
    try {
      const staffRef = doc(db, 'brands', brandId, 'staff', staffId);
      
      // Add updated timestamp
      const dataToUpdate = {
        ...staffData,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(staffRef, dataToUpdate);
      return { id: staffId, ...dataToUpdate };
    } catch (error) {
      console.error('Error updating staff member:', error);
      throw error;
    }
  },

  /**
   * Delete a staff member
   * @param {string} brandId - Brand ID
   * @param {string} staffId - Staff ID
   * @returns {Promise<void>}
   */
  deleteStaffMember: async (brandId, staffId) => {
    try {
      const staffRef = doc(db, 'brands', brandId, 'staff', staffId);
      await deleteDoc(staffRef);
    } catch (error) {
      console.error('Error deleting staff member:', error);
      throw error;
    }
  },

  /**
   * Get available permissions for staff members
   * @returns {Array} - Array of available permissions
   */
  getAvailablePermissions: () => {
    return [
      {
        module: 'campaigns',
        name: 'Campaigns',
        permissions: [
          { id: 'campaigns_view', name: 'View Campaigns' },
          { id: 'campaigns_create', name: 'Create Campaigns' },
          { id: 'campaigns_edit', name: 'Edit Campaigns' },
          { id: 'campaigns_delete', name: 'Delete Campaigns' }
        ]
      },
      {
        module: 'influencers',
        name: 'Influencers',
        permissions: [
          { id: 'influencers_view', name: 'View Influencers' },
          { id: 'influencers_invite', name: 'Invite Influencers' },
          { id: 'influencers_manage', name: 'Manage Influencers' }
        ]
      },
      {
        module: 'content',
        name: 'Content Approval',
        permissions: [
          { id: 'content_view', name: 'View Content' },
          { id: 'content_approve', name: 'Approve Content' },
          { id: 'content_reject', name: 'Reject Content' }
        ]
      },
      {
        module: 'analytics',
        name: 'Analytics',
        permissions: [
          { id: 'analytics_view', name: 'View Analytics' },
          { id: 'analytics_export', name: 'Export Analytics' }
        ]
      },
      {
        module: 'payments',
        name: 'Payments',
        permissions: [
          { id: 'payments_view', name: 'View Payments' },
          { id: 'payments_process', name: 'Process Payments' }
        ]
      },
      {
        module: 'reports',
        name: 'Reports',
        permissions: [
          { id: 'reports_view', name: 'View Reports' },
          { id: 'reports_generate', name: 'Generate Reports' },
          { id: 'reports_export', name: 'Export Reports' }
        ]
      },
      {
        module: 'messages',
        name: 'Messages',
        permissions: [
          { id: 'messages_view', name: 'View Messages' },
          { id: 'messages_send', name: 'Send Messages' }
        ]
      }
    ];
  }
};

export default staffService;
